/**
 * Database cleanup utility for migrating to unified database
 * This will delete all old databases and clear localStorage
 */

import { logger } from '@/lib/logger';

export async function cleanupOldDatabases(): Promise<void> {
  logger.info('[DB-CLEANUP] Starting database cleanup');

  // List of old database names to delete
  const oldDatabases = [
    'ExpensesDB',         // Legacy database
    'ExpensesOfflineDB',  // Legacy offline database
    'TestExpensesDB',     // Test database
    'TestDB'              // Test database
    // Note: ExpensesDB_v2 is the CURRENT database, don't delete it here
  ];

  // Delete each old database
  for (const dbName of oldDatabases) {
    try {
      await indexedDB.deleteDatabase(dbName);
      logger.debug(`[DB-CLEANUP] Deleted database: ${dbName}`);
    } catch (error) {
      logger.debug(`[DB-CLEANUP] Could not delete ${dbName}`, { error });
    }
  }

  // Also check for any test databases with timestamps
  try {
    const databases = await indexedDB.databases();
    for (const db of databases) {
      if (db.name && (
        db.name.includes('TestExpensesDB') ||
        db.name.includes('TestDB') ||
        db.name.includes('ExpensesOfflineDB')
      )) {
        await indexedDB.deleteDatabase(db.name);
        logger.debug(`[DB-CLEANUP] Deleted test database: ${db.name}`);
      }
    }
  } catch (error) {
    logger.debug('[DB-CLEANUP] Could not list databases (not supported in this browser)');
  }

  // Clear relevant localStorage items
  const localStorageKeys = [
    'offline-expenses',
    'useLegacyExpenses',
    'useOfflineExpenses'
  ];

  for (const key of localStorageKeys) {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      logger.debug(`[DB-CLEANUP] Removed localStorage item: ${key}`);
    }
  }

  logger.debug('[DB-CLEANUP] Database cleanup complete!');
}

/**
 * Check if cleanup is needed
 * Returns true if any old databases exist
 */
export async function isCleanupNeeded(): Promise<boolean> {
  try {
    const databases = await indexedDB.databases();
    return databases.some(db =>
      db.name === 'ExpensesOfflineDB' ||
      (db.name && db.name.includes('TestExpensesDB')) ||
      (db.name && db.name.includes('TestDB'))
    );
  } catch {
    // If we can't check, assume cleanup is needed if localStorage has old flags
    return !!(
      localStorage.getItem('useLegacyExpenses') ||
      localStorage.getItem('useOfflineExpenses')
    );
  }
}

/**
 * Force reset all databases - nuclear option
 * This will delete EVERYTHING and start fresh
 */
export async function forceResetDatabases(): Promise<void> {
  logger.debug('[DB-RESET] 🔥 FORCE RESETTING ALL DATABASES...');

  // Get all databases
  try {
    const databases = await indexedDB.databases();
    for (const db of databases) {
      if (db.name) {
        try {
          await indexedDB.deleteDatabase(db.name);
          logger.debug(`[DB-RESET] Deleted: ${db.name}`);
        } catch (error) {
          logger.error(`[DB-RESET] Failed to delete ${db.name}:`, error);
        }
      }
    }
  } catch (error) {
    // Fallback: try to delete known database names
    const knownDbs = [
      'ExpensesDB',           // Old database
      'ExpensesOfflineDB',    // Old offline database
      'ExpensesDB_v2',        // New Effect-based repository database
    ];
    for (const dbName of knownDbs) {
      try {
        await indexedDB.deleteDatabase(dbName);
        logger.debug(`[DB-RESET] Deleted: ${dbName}`);
      } catch (error) {
        logger.error(`[DB-RESET] Failed to delete ${dbName}:`, error);
      }
    }
  }

  // Clear all localStorage items related to databases
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.includes('db') ||
      key.includes('database') ||
      key.includes('unified') ||
      key.includes('offline') ||
      key.includes('sync') ||
      key.includes('cleanup')
    )) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    logger.debug(`[DB-RESET] Removed localStorage: ${key}`);
  });

  logger.debug('[DB-RESET] ✅ Complete! Please refresh the page.');
  return;
}

// Auto-cleanup on first load after update
if (typeof window !== 'undefined') {
  // Expose reset function globally for emergency use
  (window as any).forceResetDatabases = forceResetDatabases;

  const CLEANUP_VERSION = 'v2.0.0'; // Increment this when we want to force cleanup
  const lastCleanup = localStorage.getItem('db_cleanup_version');

  if (lastCleanup !== CLEANUP_VERSION) {
    isCleanupNeeded().then(needed => {
      if (needed) {
        logger.debug('[DB-CLEANUP] Old databases detected, running cleanup...');
        cleanupOldDatabases().then(() => {
          localStorage.setItem('db_cleanup_version', CLEANUP_VERSION);
          logger.debug('[DB-CLEANUP] Cleanup complete, refresh recommended');
        });
      } else {
        localStorage.setItem('db_cleanup_version', CLEANUP_VERSION);
      }
    });
  }

  // Log instructions for manual reset
  logger.debug('🔧 Database Management');
  logger.debug('If you encounter database errors, run: forceResetDatabases()');
}